import { type BaseContext } from '@atlassian/state-machine/types';
import type AnalyticsWebClient from '@atlassiansox/analytics-web-client';
import { nanoid } from 'nanoid';

// todo: https://product-fabric.atlassian.net/browse/CNS-10273 - Remove dependency
// eslint-disable-next-line no-restricted-imports
import { currentSessionId } from '@canvas/canvas-state/analytics';

import { type CollabStrategy } from '../collab-strategy/index.ts';
import { type RemoteDocument } from '../connection.ts';
import { type WsSocketError } from '../network/errors.ts';
import { type Queue } from '../network/queue.ts';
import { type RelaySupplierResult } from '../network/socket.ts';
import { type StateBridge } from '../network/state-bridge.ts';
import { type Transport } from '../transport/index.ts';
import type { TransportCallbacks } from '../transport/transport.ts';

import {
  type AuthenticatedEvent,
  type SyncFetchingEvent,
} from './network-state-machine-events.ts';
import type { State } from './network-state-machine.ts';

export interface Debounce {
  debounceReconnection(): Promise<void>;
  resetAttempts(): void;
  captureError(error: Error): void;
  captureErrorState(state: State): void;
  readonly attempt: number;
  readonly onlineAttempts: number;
}

export interface PendingMessage {
  message: Uint8Array<ArrayBufferLike>;
  enqueue: boolean;
}

export interface SocketMachineContext extends BaseContext {
  remoteDocument?: RemoteDocument;
  accessToken?: string;
  relayDetails?: RelaySupplierResult;
  transport?: Transport;
  getAnalyticsWebClient?: () => Promise<AnalyticsWebClient>;
  stateBridge?: StateBridge;
  error?: Error | WsSocketError;
  sessionId?: string;
  traceId: string;
  debounce: Debounce;
  // When true, the grace-period state is skipped and the state machine transitions
  // directly to connected after starting-socket. Set by onSocketUpdateChanges when
  // there is a pending offline diff to send — in this case the connection is
  // intentional and we don't need to wait for NCS to potentially close it.
  skipGracePeriod?: boolean;

  signal: AbortSignal;
  pendingMessages: Queue<PendingMessage>;

  socketFetchSnapshot(signal: AbortSignal): Promise<RemoteDocument>;
  socketFetchSteps(
    remoteDocument: RemoteDocument,
    attempt: number,
    signal: AbortSignal,
  ): Promise<RemoteDocument>;
  socketApplyChanges(relayUrl: string, remoteDoc: RemoteDocument): RemoteDocument;
  socketUpdateChanges(
    remoteDocument: RemoteDocument,
    connectionAttempts: number,
    signal: AbortSignal,
  ): void;
  reconcileOfflineChanges?(remoteDocument: RemoteDocument): Promise<void>;
  socketConnect?(): void;
  onAckTimeout?(): void;
  onMessageSent?: TransportCallbacks['onMessageSent'];
  accessTokenSupplier(): Promise<string>;
  relaySupplier(
    attempt: number,
    signal: AbortSignal,
    collabStrategy: CollabStrategy,
  ): RelaySupplierResult;
  shouldHaltRetry(): boolean;
  shouldSkipUpdates(): boolean;
}

export function captureError<T extends Pick<SocketMachineContext, 'error'>>(
  context: T,
  event: { error: WsSocketError | Error },
) {
  context.error = event.error;
  return context;
}

export function captureAccessToken<T extends Pick<SocketMachineContext, 'accessToken'>>(
  context: T,
  event: AuthenticatedEvent,
) {
  context.accessToken = event.accessToken;
  return context;
}

export function generateTraceId<T extends Pick<SocketMachineContext, 'traceId'>>(
  context: T,
) {
  context.traceId = nanoid();
  return context;
}

export function captureSessionId<T extends Pick<SocketMachineContext, 'sessionId'>>(
  context: T,
) {
  context.sessionId = currentSessionId.sessionId;
  return context;
}

export function captureDocumentDetails<
  T extends Pick<SocketMachineContext, 'relayDetails' | 'remoteDocument'>,
>(context: T, event: SyncFetchingEvent) {
  context.relayDetails = event.relayDetails;
  context.remoteDocument = event.remoteDocument;
  return context;
}
